import React, { useState, useEffect } from "react";
import Base from "../core/Base";
import {
  Box, Typography, Paper, TextField, Button, Grid, Alert, CircularProgress,
  Checkbox, FormControlLabel, FormGroup, Divider, Chip, IconButton, Tooltip,
} from "@mui/material";
import ArrowBackIcon  from "@mui/icons-material/ArrowBack";
import AddIcon        from "@mui/icons-material/Add";
import DeleteIcon     from "@mui/icons-material/Delete";
import LockIcon       from "@mui/icons-material/Lock";
import LockOpenIcon   from "@mui/icons-material/LockOpen";
import { Link }       from "react-router-dom";
import { isAutheticated } from "../auth/helper";
import {
  updateSubAdmin, getASubAdmin, updatePagePerms,
  updateIPRestrictions, grantIPBypass, revokeIPBypass,
} from "./helper/adminapicall";

const GRANTABLE_PAGES = [
  { key: "subadmins",  label: "Subadmins" },
  { key: "server",     label: "Server Health" },
  { key: "redis",      label: "Redis Monitor" },
  { key: "login-logs", label: "Login Logs" },
  { key: "sources",    label: "Feed Sources" },
  { key: "companies",  label: "Companies" },
];

const UpdateSubadmin = ({ match }) => {
  const { admin, token } = isAutheticated();
  const [values, setValues] = useState({
    name: "", lastname: "", phone: "", email: "",
    password: "", confirmPassword: "", error: "", loading: false, success: false,
  });
  const { name, lastname, phone, email, password, confirmPassword, error, loading, success } = values;

  const [allowedPages,    setAllowedPages]    = useState([]);
  const [pagePermsError,  setPagePermsError]  = useState("");
  const [pagePermsSaving, setPagePermsSaving] = useState(false);
  const [pagePermsOk,     setPagePermsOk]     = useState(false);

  const [allowedIPs,    setAllowedIPs]    = useState([]);
  const [ipBypassOnce,  setIpBypassOnce]  = useState(false);
  const [newIP,         setNewIP]         = useState("");
  const [ipSaving,      setIpSaving]      = useState(false);
  const [ipMsg,         setIpMsg]         = useState({ text: "", severity: "success" });
  const [bypassWorking, setBypassWorking] = useState(false);

  useEffect(() => {
    getASubAdmin(admin._id, token, match.params.subadminId).then((data) => {
      if (!data?.error) {
        setValues((v) => ({ ...v, name: data.name || "", lastname: data.lastname || "", phone: data.phone || "", email: data.email || "" }));
        if (Array.isArray(data.allowedPages)) setAllowedPages(data.allowedPages);
        if (Array.isArray(data.allowedIPs))   setAllowedIPs(data.allowedIPs);
        setIpBypassOnce(!!data.ipBypassOnce);
      }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (field) => (e) => setValues({ ...values, error: "", [field]: e.target.value });
  const togglePage   = (key) => setAllowedPages((prev) =>
    prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
  );

  const savePagePerms = () => {
    setPagePermsError(""); setPagePermsSaving(true); setPagePermsOk(false);
    updatePagePerms(admin._id, token, match.params.subadminId, { allowedPages })
      .then((data) => {
        setPagePermsSaving(false);
        if (data?.error) setPagePermsError(data.error);
        else setPagePermsOk(true);
      })
      .catch(() => { setPagePermsSaving(false); setPagePermsError("Failed to update permissions"); });
  };

  const IP_RE = /^(\d{1,3}\.){3}\d{1,3}$|^[\da-fA-F:]+$/;

  const addIPToList = () => {
    const ip = newIP.trim();
    if (!ip || !IP_RE.test(ip)) return setIpMsg({ text: "Enter a valid IP address", severity: "error" });
    if (allowedIPs.includes(ip)) return setIpMsg({ text: "IP already in list", severity: "warning" });
    setAllowedIPs((prev) => [...prev, ip]);
    setNewIP("");
    setIpMsg({ text: "", severity: "success" });
  };

  const removeIPFromList = (ip) => setAllowedIPs((prev) => prev.filter((x) => x !== ip));

  const saveIPRestrictions = () => {
    setIpSaving(true); setIpMsg({ text: "", severity: "success" });
    updateIPRestrictions(admin._id, token, match.params.subadminId, { allowedIPs })
      .then((d) => {
        setIpSaving(false);
        if (d?.error) setIpMsg({ text: d.error, severity: "error" });
        else setIpMsg({ text: "IP restrictions saved.", severity: "success" });
      })
      .catch(() => { setIpSaving(false); setIpMsg({ text: "Failed to save", severity: "error" }); });
  };

  const handleGrantBypass  = () => {
    setBypassWorking(true);
    grantIPBypass(admin._id, token, match.params.subadminId)
      .then((d) => { setBypassWorking(false); if (!d?.error) setIpBypassOnce(true); })
      .catch(() => setBypassWorking(false));
  };
  const handleRevokeBypass = () => {
    setBypassWorking(true);
    revokeIPBypass(admin._id, token, match.params.subadminId)
      .then((d) => { setBypassWorking(false); if (!d?.error) setIpBypassOnce(false); })
      .catch(() => setBypassWorking(false));
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (password && password !== confirmPassword) return setValues({ ...values, error: "Passwords do not match" });
    setValues({ ...values, error: "", loading: true });
    updateSubAdmin(admin._id, token, match.params.subadminId, { name, lastname, phone, email, password })
      .then((data) => {
        if (data?.error) setValues({ ...values, error: data.error, loading: false });
        else setValues({ ...values, error: "", loading: false, success: true });
      })
      .catch(() => setValues({ ...values, error: "Update failed", loading: false }));
  };

  return (
    <Base>
      <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
        <Button startIcon={<ArrowBackIcon />} component={Link} to="/subadmins" sx={{ color: "text.secondary" }}>
          Back
        </Button>
        <Box>
          <Typography variant="h5">Update Subadmin</Typography>
          <Typography variant="body2" color="text.secondary">Edit subadmin account details and permissions</Typography>
        </Box>
      </Box>

      {/* Basic info */}
      <Paper elevation={0} sx={{ p: 3, border: "1px solid", borderColor: "divider", borderRadius: 3, maxWidth: 640 }}>
        {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>Subadmin updated successfully.</Alert>}
        <Box component="form" onSubmit={onSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="First Name" value={name} onChange={handleChange("name")} required /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Last Name"  value={lastname} onChange={handleChange("lastname")} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Email" type="email" value={email} onChange={handleChange("email")} required /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Phone" value={phone} onChange={handleChange("phone")} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="New Password" type="password" value={password} onChange={handleChange("password")} helperText="Leave blank to keep current" /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Confirm Password" type="password" value={confirmPassword} onChange={handleChange("confirmPassword")} /></Grid>
            <Grid item xs={12} sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 1 }}>
              <Button component={Link} to="/subadmins" sx={{ color: "text.secondary" }}>Cancel</Button>
              <Button type="submit" variant="contained" disabled={loading}>
                {loading ? <CircularProgress size={20} color="inherit" /> : "Save Changes"}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* Page access permissions */}
      <Paper elevation={0} sx={{ p: 3, border: "1px solid", borderColor: "divider", borderRadius: 3, maxWidth: 640, mt: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>Page Access Permissions</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Grant access to admin-only pages for this subadmin.
        </Typography>
        <Divider sx={{ mb: 2 }} />
        {pagePermsError && <Alert severity="error"   sx={{ mb: 2 }}>{pagePermsError}</Alert>}
        {pagePermsOk    && <Alert severity="success" sx={{ mb: 2 }}>Page permissions updated.</Alert>}
        <FormGroup>
          {GRANTABLE_PAGES.map((pg) => (
            <FormControlLabel
              key={pg.key}
              control={<Checkbox checked={allowedPages.includes(pg.key)} onChange={() => togglePage(pg.key)} size="small" />}
              label={<Typography variant="body2">{pg.label}</Typography>}
            />
          ))}
        </FormGroup>
        <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
          <Button variant="contained" onClick={savePagePerms} disabled={pagePermsSaving}>
            {pagePermsSaving ? <CircularProgress size={20} color="inherit" /> : "Save Permissions"}
          </Button>
        </Box>
      </Paper>

      {/* IP restrictions */}
      <Paper elevation={0} sx={{ p: 3, border: "1px solid", borderColor: "divider", borderRadius: 3, maxWidth: 640, mt: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <LockIcon fontSize="small" color="action" />
          <Typography variant="subtitle1" fontWeight={600}>IP Restriction</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Lock this account to specific IP addresses. Leave empty to allow login from any IP.
        </Typography>
        <Divider sx={{ mb: 2 }} />
        {allowedIPs.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Login restricted to {allowedIPs.length} IP{allowedIPs.length > 1 ? "s" : ""}. Other IPs will be blocked.
          </Alert>
        )}
        {ipMsg.text && <Alert severity={ipMsg.severity} sx={{ mb: 2 }}>{ipMsg.text}</Alert>}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2, minHeight: 32 }}>
          {allowedIPs.length === 0
            ? <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>No IPs added — all IPs allowed.</Typography>
            : allowedIPs.map((ip) => (
                <Chip
                  key={ip} label={ip} size="small"
                  onDelete={() => removeIPFromList(ip)}
                  deleteIcon={<Tooltip title="Remove"><DeleteIcon /></Tooltip>}
                />
              ))
          }
        </Box>
        <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
          <TextField
            size="small" placeholder="e.g. 192.168.1.10" value={newIP}
            onChange={(e) => setNewIP(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addIPToList())}
            sx={{ flex: 1 }}
          />
          <Button variant="outlined" startIcon={<AddIcon />} onClick={addIPToList}>Add</Button>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 3 }}>
          <Button variant="contained" onClick={saveIPRestrictions} disabled={ipSaving}>
            {ipSaving ? <CircularProgress size={20} color="inherit" /> : "Save IP Restrictions"}
          </Button>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>One-time Bypass</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Grant a single login from any IP. Consumed automatically on next login.
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Chip
            icon={ipBypassOnce ? <LockOpenIcon /> : <LockIcon />}
            label={ipBypassOnce ? "Bypass ACTIVE" : "Bypass inactive"}
            color={ipBypassOnce ? "warning" : "default"}
            variant={ipBypassOnce ? "filled" : "outlined"}
          />
          {ipBypassOnce
            ? <Button variant="outlined" color="error" startIcon={<LockIcon />} onClick={handleRevokeBypass} disabled={bypassWorking}>
                {bypassWorking ? <CircularProgress size={18} color="inherit" /> : "Revoke Bypass"}
              </Button>
            : <Button variant="outlined" color="warning" startIcon={<LockOpenIcon />} onClick={handleGrantBypass} disabled={bypassWorking}>
                {bypassWorking ? <CircularProgress size={18} color="inherit" /> : "Grant Bypass"}
              </Button>
          }
        </Box>
      </Paper>
    </Base>
  );
};

export default UpdateSubadmin;
